/**
 * 展示层类型。
 *
 * 分三层，因为它们的**真源不同**：
 * 1. 契约 DTO（`Message`/`Conversation`…）——服务端说了算，绝不在这里改形状；
 * 2. `MessageViewModel`——把 DTO 摊平成模板友好的形状，并把"哪些字段缺失"显式化成
 *    `null`（模板里 `v-if` 比 `v-if="message.estimated !== null"` 这种判断可靠得多）；
 * 3. `ChatListItem`——列表渲染项。历史消息用服务端 id 当 key，而乐观插入的用户消息
 *    与流式草稿还没有 id，所以它们各自是独立分支，key 是本地生成的。
 *
 * 为什么要区分 `MessageViewModel` 与 `AssistantDraft`：草稿的 `content` 每个
 * delta 都在变，字段含义也不同（`finishReason` 只对流式回复有意义），混成一个大
 * 可选字段的类型会让每个使用点都得做类型收窄。
 *
 * v1.3.0 起**没有**用量字段：`Message` 不再带 token 数，`usage` 帧也只带
 * `estimated` 一个信号。气泡上唯一还能说的是"这轮的数字是估算的"。
 */

import type {
  AgeBand,
  ChatFinishReason,
  Conversation,
  Domain,
  Message,
  SafetyFlag,
} from '@spirit/contracts';

/** 消息在界面上的完整形状。 */
export interface MessageViewModel {
  id: string | null;
  role: 'user' | 'assistant';
  content: string;
  domain: Domain | null;
  ageBand: AgeBand | null;
  safetyFlag: SafetyFlag | null;
  /**
   * 这一轮的消耗是否为估算值（来自 SSE 的 `usage` 帧）。
   *
   * `null` 表示"不知道"——历史消息没有这个字段（契约的 `Message` 里没有它），
   * 而未收到 `usage` 帧的草稿同样不知道。与 `false` 的区别是刻意的：
   * `false` 代表服务端明确说过"这是实测值"，那就不该再打「估算」标。
   */
  estimated: boolean | null;
  latencyMs: number | null;
  upstreamModel: string | null;
  createdAt: string | null;
  streaming?: boolean;
  aborted?: boolean;
  errorMessage?: string | null;
  /** 命中安全策略时的公开话术（与 errorMessage 分开渲染，样式不同）。 */
  safetyMessage?: string | null;
  /**
   * 整条回答被服务端撤回（`retract` 帧）。
   *
   * 与 `errorMessage` 的区别：撤回时回答**已经完整显示过**，正文会被清空、
   * 只留一张安全提示卡；错误则是"这一轮什么都没得到"。
   */
  retracted?: boolean;
}

/**
 * DTO → 视图模型。
 *
 * `estimated` 只能来自流式 `usage` 帧，所以历史消息一律是 `null`：
 * 服务端刻意不把用量写进消息对象（前端拿不到 token 量，也就无从判断是否为估算）。
 */
export function messageToViewModel(message: Message): MessageViewModel {
  return {
    id: message.id,
    role: message.role === 'assistant' ? 'assistant' : 'user',
    content: message.content,
    domain: message.domain,
    ageBand: message.ageBand,
    safetyFlag: message.safetyFlag,
    estimated: null,
    latencyMs: message.latencyMs > 0 ? message.latencyMs : null,
    upstreamModel: message.upstreamModel,
    createdAt: message.createdAt,
  };
}

/** 正在流式接收的助手回复。 */
export interface AssistantDraft extends MessageViewModel {
  role: 'assistant';
  finishReason: ChatFinishReason | null;
}

/** 一条全新的空草稿。 */
export function createAssistantDraft(): AssistantDraft {
  return {
    id: null,
    role: 'assistant',
    content: '',
    domain: null,
    ageBand: null,
    safetyFlag: null,
    estimated: null,
    latencyMs: null,
    upstreamModel: null,
    createdAt: null,
    streaming: true,
    aborted: false,
    errorMessage: null,
    safetyMessage: null,
    finishReason: null,
  };
}

/**
 * 乐观插入的用户消息。
 *
 * `key` 与 `id` 刻意分开：列表 key 必须从第一帧起就稳定（用 id 的话，
 * 用户消息在 `meta` 帧到达前 key 是 null，列表会整段重建），
 * 而 `id` 是服务端稍后补上的真值。
 */
export interface LocalUserMessage {
  key: string;
  id: string | null;
  content: string;
  domain: Domain | null;
  ageBand: AgeBand | null;
}

/** 聊天列表项。 */
export type ChatListItem =
  | { kind: 'message'; key: string; view: MessageViewModel }
  | { kind: 'local-user'; key: string; local: LocalUserMessage }
  | { kind: 'assistant'; key: string; draft: AssistantDraft };

/** 会话列表页的加载态。 */
export interface ConversationListState {
  loading: boolean;
  error: string | null;
  items: Conversation[];
  nextCursor: string | null;
}

/** 通用加载态。 */
export interface LoadState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}
